import { Controller } from '@hotwired/stimulus'
import { omit } from 'lodash'

/**
 * Usage: <%= form_with(url:, model:, method: :post, html: { data: { controller: 'form' } }) do |f| %>
 * */

export default class extends Controller {
  static targets = ['submitButton']
  static values = {
    onlyEnableSubmitWhenDirty: { type: Boolean, default: false }
  }

  connect() {
    this.element.setAttribute('novalidate', true)
    this.initialValues = this.getFormValues() // Capture initial form state
    this.dirty = false

    // Add event listeners
    this.element.addEventListener('blur', this.onBlur, true)
    this.element.addEventListener('submit', this.onSubmit)
    this.element.addEventListener('ajax:beforeSend', this.onSubmit)
    this.element.addEventListener('change', this.onInputChange)
    this.element.addEventListener('input', this.onInputChange)

    this.#disableSubmit(true)
    this.validateSubmit()
  }

  disconnect() {
    // Remove event listeners
    this.element.removeEventListener('blur', this.onBlur)
    this.element.removeEventListener('submit', this.onSubmit)
    this.element.removeEventListener('ajax:beforeSend', this.onSubmit)
    this.element.removeEventListener('change', this.onInputChange)
    this.element.removeEventListener('input', this.onInputChange)
  }

  onBlur = (event) => {
    this.validateField(event.target)
  }

  onInputChange = (_event) => {
    this.checkDirty()
    this.validateSubmit()
  }

  validateSubmit = () => {
    const isInvalid = this.firstInvalidField != null
    const shouldDisable = this.onlyEnableSubmitWhenDirtyValue ? !this.dirty || isInvalid : isInvalid

    this.#disableSubmit(shouldDisable)
  }

  onSubmit = (event) => {
    if (!this.validateForm()) {
      event.preventDefault()
      this.firstInvalidField.focus()
    }
  }

  validateForm() {
    let isValid = true

    // Validate all fields
    this.formFields.forEach((field) => {
      if (this.shouldValidateField(field) && !this.validateField(field)) isValid = false
    })

    return isValid
  }

  validateField(field) {
    if (!this.shouldValidateField(field)) return true
    if (typeof field.checkValidity !== 'function') return true

    const isValid = field.checkValidity()
    field.classList.toggle('invalid', !isValid)

    if (field.labels != null && field.labels[0] != null) field.labels[0].classList.toggle('invalid', !isValid)

    if (!isValid) this.#disableSubmit(true)

    this.refreshErrorForInvalidField(field, isValid)
    return isValid
  }

  shouldValidateField(field) {
    return (
      !field.disabled &&
      !['file', 'reset', 'submit', 'button'].includes(field.type)
      && typeof field.checkValidity === 'function'
    )
  }

  refreshErrorForInvalidField(field, isValid) {
    this.removeExistingErrorMessage(field)
    if (!isValid) this.showErrorForInvalidField(field)
  }

  removeExistingErrorMessage(field) {
    const fieldContainer = field.closest('.field')
    if (!fieldContainer) return

    const existingErrorMessageElement = fieldContainer.querySelector('.error')

    if (existingErrorMessageElement)
      existingErrorMessageElement.parentNode.removeChild(existingErrorMessageElement)
  }

  showErrorForInvalidField(field) {
    const fieldContainer = field.closest('.field')
    if (!fieldContainer) return
    fieldContainer.insertAdjacentHTML('beforeend', this.buildFieldErrorHtml(field))
  }

  buildFieldErrorHtml(field) {
    return `<div class="error">${field.validationMessage}</div>`
  }

  checkDirty() {
    const currentValues = this.getFormValues()
    const isDirty = JSON.stringify(currentValues) !== JSON.stringify(this.initialValues)
    this.dirty = isDirty
  }

  getFormValues() {
    return omit(
      this.formFields.reduce((acc, field) => {
        if (!this.shouldValidateField(field)) return acc

        if (field.type === 'checkbox' || field.type === 'radio') {
          acc[field.name] = field.checked
        } else {
          acc[field.name] = field.value
        }

        return acc
      }, {}),
      ['authenticity_token']
    )
  }

  get formFields() {
    return Array.from(this.element.elements)
  }

  get firstInvalidField() {
    return this.formFields.find(field => typeof field.checkValidity === 'function' && !field.checkValidity())
  }

  get submitButton() {
    if (this.hasSubmitButtonTarget) return null
    return this.element.querySelector('input[type="submit"]')
  }

  // Intended to use when declaring the form.
  //
  // Example:
  //   <%=
  //     form_with(
  //       url: url, model: model, method: method,
  //       html: {
  //         id: 'sample-id',
  //         data: { controller: 'form', action: 'turbo:submit-end->form#reDisableSubmit' }
  //       }
  //     ) do |f|
  //   %>
  //     <%# ... %>
  //   <% end %>
  reDisableSubmit(event) {
    if (event.detail.fetchResponse.response.redirected === true) this.#disableSubmit(true)
  }

  resetDirtyState() {
    this.initialValues = this.getFormValues()
    this.dirty = false
  }

  #disableSubmit(isDisabled) {
    if (!!this.submitButton) this.submitButton.disabled = isDisabled
    if (this.hasSubmitButtonTarget) this.submitButtonTargets.forEach(btn => (btn.disabled = isDisabled))
  }
}
